/** @module @liquidefy/code-editor/terminal/terminal-recording @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  RecordingState,
  RecordingStatus,
  RecordingFrame,
  RecordingFrameId,
  RecordingMetadata,
  RecordingBookmark,
  RecordingExportFormat,
  RecordingId,
  TerminalId,
  PlaybackState,
  FrameType,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asRecordingFrameId,
  asRecordingId,
  now,
  DEFAULT_RECORDING_CONFIG,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 RECORDING LIFECYCLE — start, pause, resume, stop, reset, isRecording, isPaused
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Valid recording status transitions.
 */
export const VALID_RECORDING_TRANSITIONS: Record<RecordingStatus, RecordingStatus[]> = {
  'idle': ['recording'],
  'recording': ['paused', 'stopped'],
  'paused': ['recording', 'stopped'],
  'stopped': ['idle', 'exporting'],
  'exporting': ['stopped', 'idle'],
};

/**
 * Start recording.
 */
export function startRecording(state: RecordingState): EditorResult<RecordingState> {
  if (state.status !== 'idle') {
    return err('RECORDING_NOT_IDLE', `Cannot start recording in '${state.status}' state`);
  }
  const updated: RecordingState = {
    ...state,
    status: 'recording',
    frames: [],
    metadata: {
      ...state.metadata,
      startedAt: now(),
      endedAt: undefined,
      frameCount: 0,
      commandCount: 0,
      duration: 0,
    },
  };
  return ok(updated, 'RECORDING_STARTED', 'Recording started');
}

/**
 * Pause recording.
 */
export function pauseRecording(state: RecordingState): EditorResult<RecordingState> {
  if (state.status !== 'recording') {
    return err('RECORDING_NOT_ACTIVE', `Cannot pause recording in '${state.status}' state`);
  }
  return ok(
    { ...state, status: 'paused' as RecordingStatus },
    'RECORDING_PAUSED',
    'Recording paused'
  );
}

/**
 * Resume a paused recording.
 */
export function resumeRecording(state: RecordingState): EditorResult<RecordingState> {
  if (state.status !== 'paused') {
    return err('RECORDING_NOT_PAUSED', `Cannot resume recording in '${state.status}' state`);
  }
  return ok(
    { ...state, status: 'recording' as RecordingStatus },
    'RECORDING_RESUMED',
    'Recording resumed'
  );
}

/**
 * Stop recording.
 */
export function stopRecording(state: RecordingState): EditorResult<RecordingState> {
  if (state.status !== 'recording' && state.status !== 'paused') {
    return err('RECORDING_NOT_ACTIVE', `Cannot stop recording in '${state.status}' state`);
  }
  const endedAt = now();
  const duration = computeDuration(state.frames);
  const commandCount = state.frames.filter(f => f.type === 'command').length;

  const updated: RecordingState = {
    ...state,
    status: 'stopped',
    metadata: {
      ...state.metadata,
      endedAt,
      duration,
      frameCount: state.frames.length,
      commandCount,
    },
  };
  return ok(updated, 'RECORDING_STOPPED', 'Recording stopped');
}

/**
 * Reset recording to idle state, clearing all frames.
 */
export function resetRecording(state: RecordingState): EditorResult<RecordingState> {
  const updated: RecordingState = {
    ...state,
    status: 'idle',
    frames: [],
    metadata: {
      ...state.metadata,
      duration: 0,
      frameCount: 0,
      commandCount: 0,
      startedAt: 0,
      endedAt: undefined,
    },
  };
  return ok(updated, 'RECORDING_RESET', 'Recording reset');
}

/**
 * Check if recording is active.
 */
export function isRecording(state: RecordingState): boolean {
  return state.status === 'recording';
}

/**
 * Check if recording is paused.
 */
export function isPaused(state: RecordingState): boolean {
  return state.status === 'paused';
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FRAME MANAGEMENT — addDataFrame, addMarkerFrame, addCommandFrame, addResizeFrame, queries
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a data frame (terminal output) to the recording.
 */
export function addDataFrame(
  state: RecordingState,
  data: string,
  timestamp: number
): EditorResult<RecordingState> {
  return addFrameImpl(state, 'data', data, timestamp);
}

/**
 * Add a marker frame (a user-placed label) to the recording.
 */
export function addMarkerFrame(
  state: RecordingState,
  label: string,
  timestamp: number
): EditorResult<RecordingState> {
  return addFrameImpl(state, 'marker', label, timestamp);
}

/**
 * Add a command frame to the recording.
 */
export function addCommandFrame(
  state: RecordingState,
  command: string,
  timestamp: number
): EditorResult<RecordingState> {
  return addFrameImpl(state, 'command', command, timestamp);
}

/**
 * Add a resize frame to the recording.
 */
export function addResizeFrame(
  state: RecordingState,
  cols: number,
  rows: number,
  timestamp: number
): EditorResult<RecordingState> {
  return addFrameImpl(state, 'resize', `${cols}x${rows}`, timestamp);
}

/**
 * Get the total number of frames.
 */
export function frameCount(state: RecordingState): number {
  return state.frames.length;
}

/**
 * Get a frame by index.
 */
export function getFrame(state: RecordingState, index: number): RecordingFrame | undefined {
  return state.frames[index];
}

/**
 * Get frames between two timestamps.
 */
export function getFramesBetween(
  state: RecordingState,
  startTime: number,
  endTime: number
): RecordingFrame[] {
  return state.frames.filter(f => f.timestamp >= startTime && f.timestamp <= endTime);
}

/**
 * Compute the total duration of a recording from its frames.
 */
export function computeDuration(frames: RecordingFrame[]): number {
  if (frames.length === 0) return 0;
  const first = frames[0]!.timestamp;
  const last = frames[frames.length - 1]!.timestamp;
  return last - first;
}

/**
 * Compress frames by merging rapid sequential data frames.
 * Frames within the threshold (ms) are concatenated.
 */
export function compressFrames(
  frames: RecordingFrame[],
  threshold: number
): RecordingFrame[] {
  if (frames.length <= 1) return frames;

  const result: RecordingFrame[] = [];
  let current: RecordingFrame = frames[0]!;

  for (let i = 1; i < frames.length; i++) {
    const next = frames[i]!;

    if (
      current.type === 'data' &&
      next.type === 'data' &&
      (next.timestamp - current.timestamp) <= threshold
    ) {
      // Merge the data
      current = {
        ...current,
        data: current.data + next.data,
        duration: (next.timestamp - current.timestamp) + (current.duration ?? 0),
      };
    } else {
      result.push(current);
      current = next;
    }
  }
  result.push(current);

  return result;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 BOOKMARKS — addBookmark, removeBookmark, getBookmarks, nearestBookmark, sortBookmarks
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a bookmark at a frame index.
 */
export function addBookmark(
  state: RecordingState,
  label: string,
  frameIndex: number
): RecordingState {
  const bookmark: RecordingBookmark = {
    id: generateId('bookmark'),
    frameIndex,
    label,
    timestamp: state.frames[frameIndex]?.timestamp ?? now(),
  };
  // Store bookmarks in metadata.tags as serialized JSON entries
  // Since RecordingState doesn't have a bookmarks field, we use a convention
  // of storing them in the tags array with a bookmark: prefix
  const tag = `bookmark:${bookmark.id}:${bookmark.frameIndex}:${bookmark.label}`;
  return {
    ...state,
    metadata: {
      ...state.metadata,
      tags: [...state.metadata.tags, tag],
    },
  };
}

/**
 * Remove a bookmark by ID.
 */
export function removeBookmark(
  state: RecordingState,
  bookmarkId: string
): RecordingState {
  const tags = state.metadata.tags.filter(t => !t.startsWith(`bookmark:${bookmarkId}:`));
  return {
    ...state,
    metadata: { ...state.metadata, tags },
  };
}

/**
 * Get all bookmarks from the recording state.
 */
export function getBookmarks(state: RecordingState): RecordingBookmark[] {
  return state.metadata.tags
    .filter(t => t.startsWith('bookmark:'))
    .map(parseBookmarkTag)
    .filter((b): b is RecordingBookmark => b !== null);
}

/**
 * Find the nearest bookmark to a given frame index.
 */
export function nearestBookmark(
  state: RecordingState,
  frameIndex: number
): RecordingBookmark | undefined {
  const bookmarks = getBookmarks(state);
  if (bookmarks.length === 0) return undefined;

  let nearest = bookmarks[0]!;
  let minDist = Math.abs(nearest.frameIndex - frameIndex);

  for (let i = 1; i < bookmarks.length; i++) {
    const b = bookmarks[i]!;
    const dist = Math.abs(b.frameIndex - frameIndex);
    if (dist < minDist) {
      nearest = b;
      minDist = dist;
    }
  }

  return nearest;
}

/**
 * Sort bookmarks by frame index.
 */
export function sortBookmarks(bookmarks: RecordingBookmark[]): RecordingBookmark[] {
  return [...bookmarks].sort((a, b) => a.frameIndex - b.frameIndex);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 PLAYBACK — createPlayback, start, pause, seek, speed, advance, complete, progress
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new playback state for a recording.
 */
export function createPlayback(
  recordingId: RecordingId,
  _frames: RecordingFrame[]
): PlaybackState {
  return {
    recording: recordingId,
    currentFrame: 0,
    speed: 1.0,
    isPlaying: false,
    isPaused: false,
    startTime: 0,
  };
}

/**
 * Start playback.
 */
export function startPlayback(state: PlaybackState): PlaybackState {
  return {
    ...state,
    isPlaying: true,
    isPaused: false,
    startTime: now(),
  };
}

/**
 * Pause playback.
 */
export function pausePlayback(state: PlaybackState): PlaybackState {
  return {
    ...state,
    isPlaying: false,
    isPaused: true,
  };
}

/**
 * Seek to a specific frame index.
 */
export function seekToFrame(state: PlaybackState, index: number): PlaybackState {
  return {
    ...state,
    currentFrame: Math.max(0, index),
  };
}

/**
 * Seek to a specific timestamp, finding the nearest frame.
 */
export function seekToTime(
  state: PlaybackState,
  timestamp: number,
  frames: RecordingFrame[]
): PlaybackState {
  if (frames.length === 0) return state;

  let bestIdx = 0;
  let bestDist = Math.abs(frames[0]!.timestamp - timestamp);

  for (let i = 1; i < frames.length; i++) {
    const dist = Math.abs(frames[i]!.timestamp - timestamp);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }

  return { ...state, currentFrame: bestIdx };
}

/**
 * Set the playback speed multiplier.
 */
export function setPlaybackSpeed(state: PlaybackState, speed: number): PlaybackState {
  const clamped = Math.max(0.1, Math.min(16, speed));
  return { ...state, speed: clamped };
}

/**
 * Advance playback by deltaMs (wall-clock time), returning frames to render.
 */
export function advancePlayback(
  state: PlaybackState,
  deltaMs: number,
  frames: RecordingFrame[]
): { state: PlaybackState; framesToRender: RecordingFrame[] } {
  if (!state.isPlaying || frames.length === 0) {
    return { state, framesToRender: [] };
  }

  const scaledDelta = deltaMs * state.speed;
  const currentTimestamp = frames[state.currentFrame]?.timestamp ?? 0;
  const targetTimestamp = currentTimestamp + scaledDelta;

  const toRender: RecordingFrame[] = [];
  let idx = state.currentFrame;

  while (idx < frames.length && frames[idx]!.timestamp <= targetTimestamp) {
    toRender.push(frames[idx]!);
    idx++;
  }

  const newState: PlaybackState = {
    ...state,
    currentFrame: Math.min(idx, frames.length - 1),
  };

  return { state: newState, framesToRender: toRender };
}

/**
 * Check if playback has reached the end.
 */
export function isPlaybackComplete(state: PlaybackState, totalFrames: number): boolean {
  return state.currentFrame >= totalFrames - 1;
}

/**
 * Get playback progress as a value between 0 and 1.
 */
export function playbackProgress(state: PlaybackState, totalFrames: number): number {
  if (totalFrames <= 1) return state.currentFrame >= 1 ? 1 : 0;
  return state.currentFrame / (totalFrames - 1);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 EXPORT — exportAsciinema, exportTextLog, exportVTT, sizes, metadata, MIME types
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Export recording as asciinema v2 JSON format.
 */
export function exportAsciinema(state: RecordingState): string {
  const header = {
    version: 2,
    width: 80,
    height: 24,
    timestamp: Math.floor(state.metadata.startedAt / 1000),
    duration: state.metadata.duration / 1000,
    title: state.metadata.title ?? 'Terminal Recording',
    env: { SHELL: '/bin/bash', TERM: 'xterm-256color' },
  };

  const lines: string[] = [JSON.stringify(header)];
  const baseTime = state.frames[0]?.timestamp ?? 0;

  for (const frame of state.frames) {
    if (frame.type === 'data' || frame.type === 'command' || frame.type === 'output') {
      const elapsed = (frame.timestamp - baseTime) / 1000;
      const eventType = frame.type === 'command' ? 'i' : 'o';
      lines.push(JSON.stringify([elapsed, eventType, frame.data]));
    }
  }

  return lines.join('\n') + '\n';
}

/**
 * Export recording as plain text log.
 */
export function exportTextLog(state: RecordingState): string {
  const lines: string[] = [];
  const baseTime = state.frames[0]?.timestamp ?? 0;

  if (state.metadata.title) {
    lines.push(`# ${state.metadata.title}`);
  }
  if (state.metadata.description) {
    lines.push(`# ${state.metadata.description}`);
  }
  lines.push(`# Duration: ${(state.metadata.duration / 1000).toFixed(1)}s`);
  lines.push(`# Frames: ${state.metadata.frameCount}`);
  lines.push('');

  for (const frame of state.frames) {
    const elapsed = ((frame.timestamp - baseTime) / 1000).toFixed(3);
    switch (frame.type) {
      case 'command':
        lines.push(`[${elapsed}s] $ ${frame.data}`);
        break;
      case 'data':
      case 'output':
        lines.push(`[${elapsed}s] ${frame.data}`);
        break;
      case 'error':
        lines.push(`[${elapsed}s] ERROR: ${frame.data}`);
        break;
      case 'marker':
        lines.push(`[${elapsed}s] --- ${frame.data} ---`);
        break;
      case 'resize':
        lines.push(`[${elapsed}s] [resize: ${frame.data}]`);
        break;
      case 'metadata':
        lines.push(`[${elapsed}s] [meta: ${frame.data}]`);
        break;
    }
  }

  return lines.join('\n') + '\n';
}

/**
 * Export recording as WebVTT subtitle format.
 */
export function exportVTT(state: RecordingState): string {
  const lines: string[] = ['WEBVTT', ''];
  const baseTime = state.frames[0]?.timestamp ?? 0;
  let cueIndex = 1;

  for (let i = 0; i < state.frames.length; i++) {
    const frame = state.frames[i]!;
    if (frame.type !== 'command' && frame.type !== 'marker') continue;

    const startMs = frame.timestamp - baseTime;
    const endMs = (state.frames[i + 1]?.timestamp ?? (frame.timestamp + 5000)) - baseTime;

    const startStr = formatVTTTime(startMs);
    const endStr = formatVTTTime(endMs);

    lines.push(`${cueIndex}`);
    lines.push(`${startStr} --> ${endStr}`);
    lines.push(frame.type === 'command' ? `$ ${frame.data}` : frame.data);
    lines.push('');
    cueIndex++;
  }

  return lines.join('\n');
}

/**
 * Estimate the size in bytes for a given export format.
 */
export function computeExportSize(
  state: RecordingState,
  format: RecordingExportFormat
): number {
  const totalDataSize = state.frames.reduce((sum, f) => sum + f.data.length, 0);

  switch (format) {
    case 'asciinema':
      // JSON overhead per frame ~50 bytes + header ~200 bytes
      return totalDataSize + state.frames.length * 50 + 200;
    case 'text-log':
      // Timestamp overhead per frame ~15 bytes
      return totalDataSize + state.frames.length * 15 + 200;
    case 'vtt':
      // WebVTT overhead per cue ~40 bytes
      return totalDataSize + state.frames.length * 40 + 50;
    case 'webm':
    case 'mp4':
    case 'gif':
      // Binary formats: rough estimate based on duration
      return Math.max(totalDataSize * 2, (state.metadata.duration / 1000) * 50000);
    case 'zip':
      // Compressed: assume ~60% of text size
      return Math.floor(totalDataSize * 0.6) + 1000;
    default:
      return totalDataSize;
  }
}

/**
 * Convert recording metadata to a JSON-serializable object.
 */
export function recordingMetadataToJSON(metadata: RecordingMetadata): Record<string, unknown> {
  return {
    title: metadata.title,
    description: metadata.description,
    duration: metadata.duration,
    durationSeconds: metadata.duration / 1000,
    frameCount: metadata.frameCount,
    commandCount: metadata.commandCount,
    startedAt: metadata.startedAt,
    endedAt: metadata.endedAt,
    tags: metadata.tags,
    author: metadata.author,
  };
}

/**
 * MIME types for each export format.
 */
export const EXPORT_MIME_TYPES: Record<RecordingExportFormat, string> = {
  'asciinema': 'application/x-asciicast',
  'webm': 'video/webm',
  'mp4': 'video/mp4',
  'gif': 'image/gif',
  'text-log': 'text/plain',
  'vtt': 'text/vtt',
  'zip': 'application/zip',
};

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function addFrameImpl(
  state: RecordingState,
  type: FrameType,
  data: string,
  timestamp: number
): EditorResult<RecordingState> {
  if (state.status !== 'recording') {
    return err('RECORDING_NOT_ACTIVE', `Cannot add frame in '${state.status}' state`);
  }

  if (state.config.maxFrames && state.frames.length >= state.config.maxFrames) {
    return err('RECORDING_MAX_FRAMES', `Maximum frame count ${state.config.maxFrames} reached`);
  }

  const frame: RecordingFrame = {
    id: asRecordingFrameId(generateId('frame')),
    type,
    data,
    timestamp,
  };

  const updated: RecordingState = {
    ...state,
    frames: [...state.frames, frame],
    metadata: {
      ...state.metadata,
      frameCount: state.frames.length + 1,
      commandCount: state.metadata.commandCount + (type === 'command' ? 1 : 0),
    },
  };

  return ok(updated, 'FRAME_ADDED', `${type} frame added`);
}

function parseBookmarkTag(tag: string): RecordingBookmark | null {
  const parts = tag.split(':');
  if (parts.length < 4 || parts[0] !== 'bookmark') return null;
  const id = parts[1]!;
  const frameIndex = parseInt(parts[2]!, 10);
  const label = parts.slice(3).join(':');
  if (isNaN(frameIndex)) return null;
  return { id, frameIndex, label, timestamp: 0 };
}

function formatVTTTime(ms: number): string {
  const totalSeconds = ms / 1000;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const millis = Math.floor(ms % 1000);

  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}.${pad3(millis)}`;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function pad3(n: number): string {
  return n.toString().padStart(3, '0');
}
